import type { PDFDocument } from '@cantoo/pdf-lib';
import { ENGINE_UNAVAILABLE, PdfEngineError, type PagePlan, type PdfInfo } from './types';

export class EngineUnavailableError extends Error {
  constructor() {
    super(ENGINE_UNAVAILABLE);
  }
}

import type * as OperationsModule from './operations';

type Operations = typeof OperationsModule;

/**
 * The open files and what can be done with them; the same code in the Worker and in the engine's
 * fallback. The PDF library is imported on first use, so it never weighs on the page's main
 * bundle.
 */
export class PdfSession {
  private readonly docs = new Map<number, PDFDocument>();
  private operations: Promise<Operations> | null = null;

  /** Rejects with ENGINE_UNAVAILABLE when the library cannot be loaded (network, a stale deploy). */
  private ops(): Promise<Operations> {
    this.operations ??= import('./operations').catch(() => {
      this.operations = null;
      throw new EngineUnavailableError();
    });
    return this.operations;
  }

  async open(source: number, bytes: ArrayBuffer): Promise<PdfInfo> {
    const ops = await this.ops();
    const wasEncrypted = ops.declaresEncryption(bytes);
    const doc = await ops.openDocument(bytes);
    this.docs.set(source, doc);
    return ops.describe(doc, wasEncrypted);
  }

  async assemble(plan: readonly PagePlan[]): Promise<Uint8Array> {
    const ops = await this.ops();
    return ops.assemble(this.docs, plan);
  }

  async split(
    source: number,
    ranges: readonly (readonly [number, number])[],
  ): Promise<Uint8Array[]> {
    const ops = await this.ops();
    if (!this.docs.has(source)) throw new PdfEngineError('failed', `Unknown source ${source}`);
    const parts: Uint8Array[] = [];
    for (const [from, to] of ranges) {
      parts.push(await ops.assemble(this.docs, ops.rangePlan(source, from, to)));
    }
    return parts;
  }

  close(source: number): void {
    this.docs.delete(source);
  }

  has(source: number): boolean {
    return this.docs.has(source);
  }
}
